// Want to go (mockup 07, F4): a child pastes a map link, or stars an idea from the kit. Each want
// is a `place.wanted` event, queued offline like everything else, and reaches the grown-ups as a
// suggestion in "Needs you". A short link is expanded when there's signal; offline it's kept as a
// link for a grown-up to look at.
import { useMemo, useState } from 'react'
import { ulid } from 'ulid'
import type { Person } from '../../shared/events'
import { isShortMapLink, parseMapLink, type MapLink } from '../../shared/maps'
import type { Pack } from '../../shared/pack/schema'
import type { OwnPlace } from '../../shared/places'
import { Sheet } from '../app/Sheet'
import { useToast } from '../app/toast'
import { api, HttpError } from '../data/api'
import { record } from '../data/events'
import { useEvents } from '../data/progress'
import { myPlaces } from '../domain/places'
import { t } from '../strings/en'

/** Reads a pasted link into a place: on the phone if it can, through the worker for short links. */
async function placeFrom(text: string): Promise<OwnPlace | null> {
  const url = text.trim()
  let link: MapLink | null = parseMapLink(url)
  if (!link && isShortMapLink(url)) {
    try {
      link = await api<MapLink>(`/api/unfurl?url=${encodeURIComponent(url)}`)
    } catch (e) {
      if (e instanceof HttpError && e.status === 422) return null
      // Offline (or the link didn't open): keep it as a link for a grown-up.
      return { name: t.want.fromLink, url }
    }
  }
  if (!link) return null
  return {
    name: (link.name ?? t.want.fromLink).slice(0, 80),
    ...(link.lat !== undefined && link.lng !== undefined ? { at: [link.lat, link.lng] as [number, number] } : {}),
    ...(/^https?:/.test(url) ? { url } : {}),
  }
}

export function WantSheet({ pack, person }: { pack: Pack; person: Person }) {
  const events = useEvents()
  const mine = useMemo(() => myPlaces(events, person.id), [events, person.id])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameOf = (id: string, own: OwnPlace | null) => own?.name ?? pack.places.find((p) => p.id === id)?.name ?? id

  const add = async () => {
    setBusy(true)
    setError(null)
    const place = await placeFrom(text)
    setBusy(false)
    if (!place) return setError(t.want.noPlace)
    await record('place.wanted', { placeId: `w-${ulid().toLowerCase()}`, place }, person.id)
    useToast.getState().show(t.want.added(place.name), 3000)
    setText('')
  }

  return (
    <Sheet title={t.want.title}>
      <label className="field">
        <small>{t.want.paste}</small>
        <input value={text} inputMode="url" placeholder="https://maps.app.goo.gl/…" onChange={(e) => setText(e.target.value)} />
      </label>
      {error ? <p className="caption warn">{error}</p> : null}
      <button className="btn y press" style={{ marginTop: 12 }} disabled={!text.trim() || busy} onClick={() => void add()}>
        {busy ? t.want.looking : t.want.add}
      </button>
      <p className="caption">{t.want.how}</p>

      <p className="mono sheet-sub">{t.want.mine(mine.wanted.size)}</p>
      <div className="panel sheet-panel">
        {[...mine.wanted].map(([id, own]) => (
          <div className="row" key={id}>
            <span className="fam-av">{mine.found.has(id) ? '✅' : '📍'}</span>
            <div>
              <b>{nameOf(id, own)}</b>
              <small>{mine.found.has(id) ? t.want.found : own ? t.want.yours : t.want.idea}</small>
            </div>
            <button className="btn w sm press" aria-label={t.want.remove(nameOf(id, own))} onClick={() => void record('place.unwanted', { placeId: id }, person.id)}>
              ✕
            </button>
          </div>
        ))}
        {!mine.wanted.size ? (
          <div className="row">
            <small>{t.want.none}</small>
          </div>
        ) : null}
      </div>
    </Sheet>
  )
}
